<script setup>
import { computed } from 'vue';
import Sheet from '../../Sheet.vue';
import { calculatePerformance, formatPerformance } from './utils';

const props = defineProps({
    totalTrades: {
        type: Number,
        required: true
    },
    trades: {
        type: Array,
        required: true
    }
});

const positiveTradesCount = computed(() => {
    if (!props.trades) return 0;

    return props.trades.filter((trade) => calculatePerformance(trade) > 0).length;
});

const negativeTradesCount = computed(() => {
    if (!props.trades) return 0;

    return props.trades.filter((trade) => calculatePerformance(trade) < 0).length;
});

const averageWinnerPerformance = computed(() => {
    if (!props.trades) return 0;

    const winners = props.trades.filter((trade) => calculatePerformance(trade) > 0);

    if (!winners.length) return 0;

    return winners.reduce((sum, trade) => sum + calculatePerformance(trade), 0) / winners.length;
});

const averageLoserPerformance = computed(() => {
    if (!props.trades) return 0;

    const losers = props.trades.filter((trade) => calculatePerformance(trade) < 0);

    if (!losers.length) return 0;

    return losers.reduce((sum, trade) => sum + calculatePerformance(trade), 0) / losers.length;
});

const bestPerformer = computed(() => {
    if (!props.trades?.length) return null;

    const bestTrade = props.trades.reduce((best, current) => (calculatePerformance(current) > calculatePerformance(best) ? current : best));

    const bestPerformance = calculatePerformance(bestTrade);

    if (bestPerformance <= 0) return null;

    return {
        name: bestTrade.market_symbol.name,
        performance: formatPerformance(bestPerformance)
    };
});

const metrics = computed(() => {
    const baseMetrics = [
        {
            label: 'portfolio_amount',
            value: props.totalTrades,
            color: 'text-gray-700',
            icon: 'fi-rr-list',
            info: { title: 'nav_short_term_trades', text: 'portfolio_amount_info' }
        },
        { label: 'portfolio_winner', value: positiveTradesCount.value, color: 'text-green', icon: 'fi-rr-stats' },
        { label: 'portfolio_overallprofit', value: formatPerformance(averageWinnerPerformance.value), color: 'text-green', icon: 'fi-rr-arrow-trend-up' },
        { label: 'portfolio_loser', value: negativeTradesCount.value, color: 'text-red', icon: 'fi-rr-stats' },
        { label: 'portfolio_overallloss', value: formatPerformance(averageLoserPerformance.value), color: 'text-red', icon: 'fi-rr-arrow-trend-down' }
    ];

    if (bestPerformer.value) {
        baseMetrics.push({
            label: 'portfolio_best_performer',
            value: bestPerformer.value,
            type: 'best_performer',
            color: 'text-green',
            icon: 'fi-rr-trophy-star'
        });
    }

    return baseMetrics;
});
</script>

<template>
    <Sheet compact>
        <div class="flex justify-between items-start w-full p-4 border-b border-gray-200">
            <div class="flex flex-col">
                <p class="font-bold text-lg">{{ $t('nav_short_term_trades') }}</p>
                <p class="text-sm text-gray-500">{{ $t('nav_short_term_trades_description') }}</p>
            </div>
            <InfoText :title="$t('nav_short_term_trades')" :text="$t('portfolio_trades_info')" />
        </div>

        <div class="flex flex-wrap items-stretch justify-center gap-2 p-4">
            <div
                v-for="metric in metrics"
                :key="metric.label"
                class="flex flex-col items-center justify-start text-center p-3 rounded-2xl bg-gray-50 flex-1 min-w-[140px] md:min-w-[160px]"
            >
                <div class="w-16 h-16 bg-white border border-blue/30 rounded-full flex items-center justify-center mb-2 shadow-sm">
                    <i class="text-xl text-blue/50" :class="metric.icon"></i>
                </div>

                <div class="flex-grow flex flex-col justify-center w-full">
                    <div class="h-12 flex flex-col items-center justify-center leading-tight [font-variant-numeric:tabular-nums]">
                        <template v-if="metric.type === 'best_performer'">
                            <span class="block text-xl font-bold whitespace-nowrap" :class="metric.color">
                                {{ metric.value.performance }}
                            </span>
                            <span class="block text-xs font-semibold text-gray-800 max-w-[150px] line-clamp-1">
                                {{ metric.value.name }}
                            </span>
                        </template>

                        <template v-else>
                            <span class="block text-xl font-bold whitespace-nowrap" :class="metric.color">
                                {{ metric.value }}
                            </span>

                            <span class="block text-xs font-semibold opacity-0 select-none"> placeholder </span>
                        </template>
                    </div>

                    <div class="h-5 flex items-center justify-center gap-1 mt-2">
                        <span class="text-xs text-gray-500 leading-none">{{ $t(metric.label) }}</span>
                        <InfoText v-if="metric.info" :title="$t(metric.info.title)" :text="$t(metric.info.text)" size="xs" />
                    </div>
                </div>
            </div>
        </div>
    </Sheet>
</template>
